/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';
import { IEmailDelivery } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components';
import { IInvoicePaymentSummary } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components/get-invoice-payment-summary';
import { AttributeValue, CommerceProperty, CommercePropertyValue } from '@msdyn365-commerce/retail-proxy';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { ICheckoutProps, ICheckoutResources } from '../definition-extensions/checkout.ext.props.autogenerated';
import { IFraudProtectionRequest } from '../../../proxy/DataServiceEntities.g';
import { Product } from '../../../types/purchaseAPI';
import { v4 as generateUUID } from 'uuid';
import ReCAPTCHA from 'react-google-recaptcha';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

interface IOrderSummaryExtended extends IOrderSummary {
    exemptText: string;
}

const OrderSummaryComponent: React.FC<IOrderSummaryExtended> = ({ heading, lines, exemptText }) => {
    const updatedTax = lines && React.cloneElement(lines.tax as JSX.Element, { freeText: exemptText });
    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {updatedTax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.customerAccount}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const mergeExtensionProperty = (extensionProperties: CommerceProperty[], key: string, value: CommercePropertyValue): CommerceProperty[] => {
    const index = extensionProperties.findIndex(prop => prop.Key === key);
    if (index >= 0) {
        extensionProperties[index].Value = value;
    } else {
        extensionProperties.push({ Key: key, Value: value });
    }
    return extensionProperties;
};

const RecaptchaValidation: React.FC<ICheckoutViewProps & ICheckoutProps<ICheckoutResources>> = props => {
    const {
        context: {
            app: {
                config: { googleRecaptchaSiteKey }
            }
        }
    } = props;

    const onRecaptchaChange = async (token: string | null) => {
        const cartState = props.data.checkout.result?.checkoutCart;
        if (cartState && token) {
            const extensionProperties = cartState.extensionProperties ?? [];
            const allExtensionProperties = mergeExtensionProperty(extensionProperties, 'captchaToken', { StringValue: token });
            cartState.updateExtensionProperties({ newExtensionProperties: allExtensionProperties }).catch((error: string) => {
                props.telemetry.error(error);
            });
        }
        applyFraudPrevention(props);
    };

    return (
        <div key='recaptcha' className={`recaptcha`}>
            <ReCAPTCHA sitekey={googleRecaptchaSiteKey} onChange={onRecaptchaChange} />
        </div>
    );
};

const applyFraudPrevention = async (props: ICheckoutViewProps & ICheckoutProps<ICheckoutResources>) => {
    const {
        checkoutProps: {
            moduleProps: {
                data: {
                    // @ts-ignore
                    checkout: {
                        result: {
                            _checkoutCart: { _cart }
                        }
                    }
                },
                context: {
                    request: {
                        locale,
                        device: { Type }
                    }
                }
            }
        },
        context: {
            app: {
                config: { assessmentType, isTest }
            },
            request: { clientIPAddress }
        }
    } = props;

    if (!_cart) return;

    const products: Product[] = [];
    for (const item of _cart.CartLines) {
        products.push({
            productId: item.ProductId?.toString() || '',
            salesPrice: item.NetAmountWithoutTax,
            purchasePrice: item.NetAmountWithoutTax,
            quantity: item.Quantity,
            language: locale
        });
    }

    const fraudProtectionRequest: IFraudProtectionRequest = {
        purchaseId: generateUUID(),
        assessmentType,
        customerLocalDate: new Date(),
        merchantLocalDate: new Date(),
        isTest,
        totalItemCount: products.length,
        orderType: 'regular',
        totalAmount: _cart.TotalAmount || 0,
        salesTax: _cart.TaxAmount || 0,
        deviceContext: {
            deviceContextId: generateUUID(),
            externalDeviceType: Type,
            ipAddress: clientIPAddress
        },
        productList: [],
        shippingAddress: {
            city: _cart.ShippingAddress?.City || '',
            state: _cart.ShippingAddress?.State || '',
            zipCode: _cart.ShippingAddress?.ZipCode || '',
            country: _cart.ShippingAddress?.TwoLetterISORegionName || ''
        },
        _metadata: {
            trackingId: generateUUID(),
            merchantTimeStamp: new Date()
        }
    };

    if (_cart.CustomerId) {
        fraudProtectionRequest.user = {
            userId: _cart.CustomerId,
            country: _cart.ShippingAddress?.TwoLetterISORegionName || '',
            zipCode: _cart.ShippingAddress?.ZipCode || ''
        };
    }

    const cartState = props.data.checkout.result?.checkoutCart;
    if (cartState) {
        const extensionProperties = cartState.extensionProperties ?? [];
        const allExtensionProperties = mergeExtensionProperty(extensionProperties, 'fraudPrevention', {
            StringValue: JSON.stringify(fraudProtectionRequest)
        });
        cartState.updateExtensionProperties({ newExtensionProperties: allExtensionProperties }).catch((error: string) => {
            props.telemetry.error(error);
        });
    }
};

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<ICheckoutResources>> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef,
        resources
    } = props;

    React.useEffect(() => {
        const setSalesOrigin = async () => {
            const { salesOriginAttributeName, salesOrigin } = props.context.app.config;
            const checkoutState = await getCheckoutState(props.context.actionContext);
            if (!checkoutState) {
                return;
            }
            const cart = checkoutState.checkoutCart.cart;
            const checkoutAttributes = cart.AttributeValues as AttributeValue[];
            const poNumberAttributeIndex = checkoutAttributes?.findIndex(attr => attr.Name === salesOriginAttributeName);
            if (poNumberAttributeIndex >= 0 && checkoutAttributes[poNumberAttributeIndex].TextValue !== '') {
                return;
            } else {
                const attributeObj = {
                    // @ts-ignore
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                    Name: salesOriginAttributeName,
                    TextValue: salesOrigin,
                    TextValueTranslations: []
                };
                checkoutAttributes.push(attributeObj);
            }
            await checkoutState.checkoutCart.updateAttributeValues({
                newAttributeValues: checkoutAttributes
            });
            const updatedCheckoutState = await getCheckoutState(props.context.actionContext);

            await updateAsync({ callerContext: props.context.actionContext }, updatedCheckoutState.checkoutCart.cart);
            await checkoutState.checkoutCart.refreshCart({});
        };
        setSalesOrigin();
    }, []);

    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                <RecaptchaValidation {...props} />
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && <OrderSummaryComponent {...orderSummary} exemptText={resources.exemptText} />
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
